import { getVariables } from '@atlassian/jira-business-common/src/common/utils/get-variables/index.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import {
	isGroupItem,
	getParentIssueId,
} from '@atlassian/jira-business-list-handlers/src/common/utils/hierarchy/index.tsx';
import { useItemGroupsCacheActions } from '@atlassian/jira-business-list-handlers/src/controllers/item-groups-cache/index.tsx';
import { useCacheActions } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/main.tsx';
import { createGroupingViewUpdaterCacheActions } from '@atlassian/jira-business-list-handlers/src/controllers/view-updater/utils/index.tsx';
import { useHierarchyStore } from '@atlassian/jira-business-list/src/controllers/hierarchy/hierarchy-store/index.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { CATEGORY_TYPE } from '@atlassian/jira-platform-field-config';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { getGroupVariables } from '../../services/get-item-groups';
import { LIST_VIEW_ITEM_GROUPS_QUERY } from '../../services/get-item-groups/gql';
import {
	createGroupIssueId,
	getGroupQuery,
	findItemGroup,
	createItemGroup,
} from '../../services/get-item-groups/utils';
import { LIST_VIEW_QUERY } from '../../services/get-list-view-issues/gql';
import type { ListViewIssue } from '../../services/get-list-view-issues/types';
import {
	addChildToParentListView,
	removeChildFromParentListView,
} from '../../services/get-list-view-issues/utils';
import { useHierarchyLevel } from '../../services/hierarchy-level';
import { useIssueFields } from '../get-issue-fields';
import { useGroupBy } from '../group-by';
import { useHierarchyConfig } from '../hierarchy/hierarchy-config';
import { useRankParent } from '../rank-parent';
import { useIssuesInput } from '../use-issues-input';

export const useGroupCacheActions = ({ fieldIds }: { fieldIds?: string[] } = {}) => {
	const { addToCache, removeFromCache, updateItemsInCache } = useCacheActions();
	const { addGroupItemToCache, updateGroupItemInCache } = useItemGroupsCacheActions();
	const { hierarchyParent } = useHierarchyConfig();
	const [{ rankParentIssueId }] = useRankParent();
	const input = useIssuesInput({
		groupIssueId: isGroupItem(rankParentIssueId) ? rankParentIssueId : undefined,
		parentIssueId: !isGroupItem(rankParentIssueId) ? rankParentIssueId : undefined,
	});
	let projectKey: string | undefined;
	if (ff('list-uses-new-jql-parent-project-directive_v5gfs')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const projectData = useProject();

		projectKey = projectData.key;
	}
	const [{ issueParentMap, issuesMap, childrenItems, hierarchyData }] = useHierarchyStore();
	const [groupByFieldId] = useGroupBy();

	const { jqlContext, quickFiltersJql, sorting, isHidingDoneItems } = useFilteredViewContextInput({
		view: 'list',
	});

	const groupItemsQueryData = childrenItems;
	const groups: ListViewIssue[] = hierarchyData.data;

	const cloudId = useCloudId();
	const { getAvailableFields } = useIssueFields();
	const availableFields = getAvailableFields();
	const categoryField = availableFields.find((field) => field?.type === CATEGORY_TYPE);
	const isGroupingByCategory = groupByFieldId === categoryField?.id;
	const [{ hierarchyLevel }] = useHierarchyLevel();

	const { addToCache: addToGroupCache, removeFromCache: removeFromGroupCache } =
		createGroupingViewUpdaterCacheActions({
			query: LIST_VIEW_QUERY,
			groupsQuery: LIST_VIEW_ITEM_GROUPS_QUERY,
			addToCache,
			removeFromCache,
			updateItemsInCache,
			addGroupItemToCache,
			updateGroupItemInCache,
			// make sure group items data exists in apollo cache
			canUpdateGroupCache: (groupIssueId) =>
				groupItemsQueryData ? !!groupItemsQueryData[toIssueId(groupIssueId)] : false,
			findItemGroup: (item) =>
				findItemGroup({ groups, groupByFieldId, item, isGroupingByCategory }),
			createItemGroup: (item) => createItemGroup({ groupByFieldId, item }),
			findGroupById: (groupIssueId) =>
				groups?.find((group: ListViewIssue) => groupIssueId === createGroupIssueId(group.id)),
			createVariablesForGroupItem: (groupIssueId) => ({
				cloudId,
				...getVariables({
					jqlContext,
					fieldIds: fieldIds || input.fieldIds,
					quickFiltersJql,
					sorting,
					groupQuery: getGroupQuery({ groups, groupIssueId, groupByFieldId }),
					hierarchyParent: input.hierarchyParent,
					isHidingDoneItems,
					projectIdOrKey: projectKey,
				}),
			}),
			createVariablesForGroups: () =>
				getGroupVariables({
					groupByFieldId,
					jqlContext,
				}),
			getParentOfIssueIfExist: (issueId) => {
				const maybeIssue = issuesMap.get(String(issueId));
				if (!maybeIssue) {
					return undefined;
				}
				return maybeIssue.parentIssueId ?? getParentIssueId(maybeIssue);
			},
			getPrevParentOfIssueIfExists: (issueId) => issueParentMap.get(String(issueId)),
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			onUpdateParentWithNewChild: addChildToParentListView<any>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			removeChildFromParent: removeChildFromParentListView<any>,
			createVariablesForItemWithParent: (parentIssueId) => {
				if (!hierarchyParent) {
					return {
						...getVariables({
							jqlContext,
							fieldIds: fieldIds || input.fieldIds,
							quickFiltersJql,
							sorting,
							hierarchyLevel,
							hierarchyParent,
							isHidingDoneItems,
						}),
						cloudId,
					};
				}
				return {
					...getVariables({
						jqlContext,
						fieldIds: fieldIds || input.fieldIds,
						quickFiltersJql,
						sorting,
						isHidingDoneItems,
						hierarchyParent: undefined,
						parentIssue: {
							hierarchyType: hierarchyParent,
							issueId: parentIssueId,
						},
					}),
					cloudId,
				};
			},
			// note that prevParent will be undefined if the prevParent is not expanded or does not exist
			updateIssueParentMap: (issueId, newParentId) => {
				if (newParentId) {
					issueParentMap.set(String(issueId), String(newParentId));
				} else {
					issueParentMap.delete(String(issueId));
				}
			},
		});

	return { addToGroupCache, removeFromGroupCache };
};
